import React from 'react';
import { Link } from 'react-router-dom';
import { BusinessToolsShowcase } from '@/components/BusinessToolsShowcase';
import Seo from '@/components/Seo';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MarketingNav, MarketingFooter } from '@/components/MarketingFooter';
import { Layout, Smartphone, UserCog, Calendar, Globe, Megaphone, Users, ArrowRight, Zap } from 'lucide-react';

/**
 * /features — Phase 92 (site audit fix D5.1).
 *
 * Dedicated features page with its own title/meta (previously the route
 * fell through to the homepage). Copy mirrors the homepage feature bento
 * so the product story stays consistent.
 */
const FEATURES = [
    { icon: Layout, title: 'Your own website', desc: 'A drag-and-drop landing page with your logo, brand colors, hero photo, photo gallery, video embeds, and 1-click apply CTA. Mobile-first, SEO-ready.', badge: 'Pro' },
    { icon: Smartphone, title: 'Parent portal + waivers + reminders', desc: 'Branded portal where parents enroll, sign digital waivers, and get automatic email reminders before lessons.' },
    { icon: UserCog, title: 'Staff portal, shifts, time clock & payroll', desc: 'Invite your coaches, set pay rates, schedule shifts, track hours on a mobile time clock, and export payroll (PDF/CSV) to your accountant in one click.', badge: 'Pro' },
    { icon: Calendar, title: 'Class scheduling + roster', desc: 'Build weekly class blocks, manage capacity, mark attendance, and view your week at a glance.' },
    { icon: Globe, title: 'World map of coaches', desc: 'Get found by parents searching their city. Every active SplashPad coach is pinned on the public map.' },
    { icon: Megaphone, title: 'Reach your whole client list', desc: 'Send branded broadcast emails to your entire roster in one click. No third-party CRM needed.' },
    { icon: Users, title: 'Stay connected with your coach community', desc: 'Jump back into the SSU Skool community anytime to share lesson plans, ask questions, swap pricing playbooks, and grow alongside 100+ active swim instructors.', badge: 'Community' },
];

export default function FeaturesPage() {
    return (
        <div className="min-h-screen bg-[#f4f7fb]">
            <Seo
                title="SplashPad Features — Swim School Software for Small Schools & Coaches"
                description="AI business guidance, route-aware home lessons, income bookkeeping, scheduling, waivers, and payments for swim coaches and small schools."
                canonical="https://splashpad.pro/features"
            />
            <MarketingNav />

            <section className="py-14 lg:py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <div className="mx-auto mb-12 max-w-3xl text-center">
                        <Badge className="mb-3 border-[#ffd70066] bg-[#ffd70022] text-[#1a3a5c] hover:bg-[#ffd70033]">
                            <Zap className="mr-1 h-3 w-3" /> Everything in one platform
                        </Badge>
                        <h1 className="font-display text-4xl font-bold text-[#0a1628] sm:text-5xl">
                            Everything a small swim school needs to run like a business.
                        </h1>
                        <p className="mt-4 text-base text-[#1a3a5c]">
                            Stop juggling spreadsheets, Venmo, paper waivers, group texts, and a separate payroll sheet for your staff. SplashPad replaces them all — for solo coaches and swim schools with a team.
                        </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3 md:gap-6">
                        {FEATURES.map(({ icon: Icon, title, desc, badge }) => (
                            <Card key={title} className="sd-card sd-card-hover relative p-6" data-testid={`features-page-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')}`}>
                                {badge && (
                                    <Badge className="absolute -top-2 right-4 text-[10px] font-semibold" style={{ background: '#ffd700', color: '#0a1628' }}>{badge}</Badge>
                                )}
                                <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#00d4ff]/15">
                                    <Icon className="h-5 w-5 text-[#0a1628]" />
                                </div>
                                <h2 className="font-display text-lg font-bold text-[#0a1628]">{title}</h2>
                                <p className="mt-2 text-sm text-[#1a3a5c]">{desc}</p>
                            </Card>
                        ))}
                    </div>

                    <div className="mt-12 text-center">
                        <Link
                            to="/signup?role=instructor"
                            className="sd-cta sd-focus inline-flex items-center rounded-xl px-6 py-3 text-base font-semibold"
                            data-testid="features-page-cta"
                        >
                            Start My Free 30-Day Trial <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                        <p className="mt-3 text-xs text-[#1a3a5c]/70">
                            See <Link to="/pricing" className="text-primary underline" data-testid="features-pricing-link">pricing</Link> — a monthly plan plus $0.25 per successful transaction and Stripe processing fees.
                        </p>
                    </div>
                </div>
            </section>

            <BusinessToolsShowcase context="features" />
            <MarketingFooter />
        </div>
    );
}
